import React, { Fragment } from 'react'
import { postToApi, patchToApi } from 'actions'
import { useStateValue } from 'context/store'
import { handleErrorMessage } from 'utils/customHooks/useApiRequest'
import { Grid, Typography, makeStyles, Divider } from '@material-ui/core'
import { withStyles } from '@material-ui/core/styles'
import Switch from '@material-ui/core/Switch'
import MuiAccordion from '@material-ui/core/Accordion'
import MuiAccordionSummary from '@material-ui/core/AccordionSummary'
import AccordionDetails from '@material-ui/core/AccordionDetails'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import cloneDeep from 'lodash/cloneDeep'

const useNotificationsConfigStyles = makeStyles(theme => ({
   applicationsContainer: {
      display: 'flex',
      flexDirection: 'column',
      padding: theme.spacing(0, 1, 1)
   },
   switchContainer: {
      width: '100%',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
   },
   appSwitchContainer: {
      padding: theme.spacing(1, 8, 1, 2)
   },
   subtitle: {
      marginLeft: theme.spacing(2),
      fontWeight: 'bold',
      padding: theme.spacing(1.5, 0)
   },
   subdomainName: {
      fontSize: 14
   },
   applicationName: {
      fontSize: 14
   }
}))

function NotificationsConfig({
   notificationsConfigs,
   setNotificationsConfigs
}) {
   const classes = useNotificationsConfigStyles()
   const [, dispatch] = useStateValue()

   async function handleSubdomainChange(event) {
      const { name: subdomainCode, checked } = event.target

      const lastValidState = cloneDeep(notificationsConfigs)

      setNotificationsConfigs(previous => {
         const applications = previous[subdomainCode].applications
         for (const appCode in applications) {
            applications[appCode] = {
               ...applications[appCode],
               appStatus: checked
            }
         }
         return {
            ...previous,
            [subdomainCode]: {
               ...previous[subdomainCode],
               subdomainStatus: checked,
               applications
            }
         }
      })

      try {
         // NOTA: No aplica useApiRequest por callback en el catch
         const payload = {
            code: subdomainCode,
            status: checked ? 'A' : 'I'
         }
         const url = `${process.env.REACT_APP_NOTIFICATIONS_API}/notifications/config/subdomain`
         await patchToApi(url, payload)
      } catch (error) {
         setNotificationsConfigs(lastValidState)
         dispatch({
            type: 'OPEN_SNACKBAR',
            openMessage: {
               message: `${handleErrorMessage(
                  error
               )} (Notificaciones: Cambio Subdominio)`,
               severity: 'error'
            }
         })
      }
   }

   async function handleApplicationChange(event) {
      const { name: subdomainCode_appCode, checked } = event.target
      const [subdomainCode, appCode] = subdomainCode_appCode.split('_')

      const lastValidState = cloneDeep(notificationsConfigs)

      setNotificationsConfigs(previous => {
         const currentSubdomain = previous[subdomainCode]
         const currentApplications = previous[subdomainCode].applications
         return {
            ...previous,
            [subdomainCode]: {
               ...currentSubdomain,
               applications: {
                  ...currentApplications,
                  [appCode]: {
                     ...currentApplications[appCode],
                     appStatus: checked
                  }
               }
            }
         }
      })

      try {
         // NOTA: No aplica useApiRequest por callback en el catch
         const payload = {
            code: appCode,
            status: checked ? 'A' : 'I'
         }
         const url = `${process.env.REACT_APP_NOTIFICATIONS_API}/notifications/config`
         await postToApi(url, payload)
      } catch (error) {
         setNotificationsConfigs(lastValidState)
         dispatch({
            type: 'OPEN_SNACKBAR',
            openMessage: {
               message: `${handleErrorMessage(
                  error
               )} (Notificaciones: Cambio Aplicación)`,
               severity: 'error'
            }
         })
      }
   }

   function stopSwitchClickPropagation(event) {
      event.stopPropagation()
   }

   return (
      <Grid>
         <Divider variant="middle" />
         <div className={classes.subtitle}>
            Configura tus Notificaciones aquí:
         </div>
         <Divider variant="middle" />
         {Object.keys(notificationsConfigs).map(subdomainCode => {
            const { subdomainName, subdomainStatus, applications } =
               notificationsConfigs[subdomainCode]
            return (
               <Fragment key={subdomainCode}>
                  <Accordion key={subdomainCode} square>
                     <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-label="Expand"
                     >
                        <Grid className={classes.switchContainer}>
                           <Typography className={classes.subdomainName}>
                              {subdomainName}
                           </Typography>
                           <AntSwitch
                              color={'primary'}
                              checked={subdomainStatus}
                              onChange={handleSubdomainChange}
                              name={subdomainCode}
                              onClick={stopSwitchClickPropagation}
                              onFocus={stopSwitchClickPropagation}
                           />
                        </Grid>
                     </AccordionSummary>
                     <AccordionDetails
                        className={classes.applicationsContainer}
                     >
                        {Object.keys(applications).map(applicationCode => {
                           const { appName, appStatus } =
                              applications[applicationCode]
                           return (
                              <Grid
                                 key={applicationCode}
                                 className={`${classes.switchContainer} ${classes.appSwitchContainer}`}
                              >
                                 <Typography
                                    className={classes.applicationName}
                                 >
                                    {appName}
                                 </Typography>
                                 <AntSwitch
                                    color={'primary'}
                                    checked={appStatus}
                                    onChange={handleApplicationChange}
                                    name={`${subdomainCode}_${applicationCode}`}
                                 />
                              </Grid>
                           )
                        })}
                     </AccordionDetails>
                  </Accordion>
                  <Divider variant="middle" />
               </Fragment>
            )
         })}
      </Grid>
   )
}

export default NotificationsConfig

const Accordion = withStyles({
   root: {
      border: 'none',
      boxShadow: 'none',
      '&:not(:last-child)': {
         borderBottom: 0
      },
      '&:before': {
         display: 'none'
      },
      '&$expanded': {
         margin: 'auto'
      }
   },
   expanded: {}
})(MuiAccordion)

const AccordionSummary = withStyles({
   root: {
      marginBottom: -1,
      minHeight: 45,
      '&$expanded': {
         minHeight: 45
      }
   },
   content: {
      '&$expanded': {
         margin: 0
      }
   },
   expanded: {
      '&$content': {
         '& > div > p': {
            fontWeight: 'bold'
         }
      }
   }
})(MuiAccordionSummary)

const AntSwitch = withStyles(theme => ({
   root: {
      width: 28,
      height: 16,
      padding: 0,
      display: 'flex'
   },
   switchBase: {
      padding: 2,
      color: theme.palette.common.white,
      '& + $track': {
         opacity: 1,
         backgroundColor: theme.palette.grey[500],
         borderColor: theme.palette.grey[500]
      },
      '&$checked': {
         transform: 'translateX(12px)',
         color: theme.palette.common.white,
         '& + $track': {
            opacity: 1,
            backgroundColor: theme.palette.primary.main,
            borderColor: theme.palette.primary.main
         }
      }
   },
   thumb: {
      width: 12,
      height: 12,
      boxShadow: 'none'
   },
   track: {
      border: `1px solid ${theme.palette.grey[500]}`,
      borderRadius: 16 / 2,
      opacity: 1,
      backgroundColor: theme.palette.common.white
   },
   checked: {}
}))(Switch)
